/* global axios */
import ApiClient from './ApiClient';

class FacebookPagesAPI extends ApiClient {
  constructor() {
    super('facebook_pages', { accountScoped: true });
  }

  get() {
    return axios.get(this.url);
  }

  getPosts(pageId, params) {
    const urlSearchParams = new URLSearchParams({
      ...(params?.sponsored && { sponsored: params.sponsored }),
      ...(params?.post_type && { post_type: params.post_type }),
      ...(params?.message && { message: params.message }),
      ...(params?.order && { order: params.order }),
      ...(params?.actives && { actives: params.actives }),
      ...(params?.source_type && { source_type: params.source_type }),
      ...(params?.folder_label && { folder_label: params.folder_label }),
      ...(params?.publicationDateStart && {
        publication_date_start: params.publicationDateStart,
      }),
      ...(params?.publicationDateEnd && {
        publication_date_end: params.publicationDateEnd,
      }),
      instagram_page: params.pagination.instagram.currentPage,
      instagram_per_page: params.pagination.instagram.perPage,
      facebook_page: params.pagination.facebook.currentPage,
      facebook_per_page: params.pagination.facebook.perPage,
    });
    return axios.get(
      `${this.url}/${pageId}/posts?${urlSearchParams.toString()}`
    );
  }

  getPost(pageId, postId) {
    return axios.get(`${this.url}/${pageId}/posts/${postId}`);
  }

  getPostsCounts(pageId, params) {
    const urlSearchParams = new URLSearchParams({
      ...(params?.sponsored && { sponsored: params.sponsored }),
      ...(params?.post_type && { post_type: params.post_type }),
      ...(params?.message && { message: params.message }),
      ...(params?.order && { order: params.order }),
      ...(params?.actives && { actives: params.actives }),
      ...(params?.source_type && { source_type: params.source_type }),
      ...(params?.folder_label && { folder_label: params.folder_label }),
      ...(params?.publicationDateStart && {
        publication_date_start: params.publicationDateStart,
      }),
      ...(params?.publicationDateEnd && {
        publication_date_end: params.publicationDateEnd,
      }),
    });
    return axios.get(
      `${this.url}/${pageId}/posts/counts?${urlSearchParams.toString()}`
    );
  }

  toggleActive(pageId, postId) {
    return axios.put(`${this.url}/${pageId}/posts/${postId}/toggle_active`);
  }

  deletePost(pageId, postId) {
    return axios.delete(`${this.url}/${pageId}/posts/${postId}`);
  }

  getPostComments(pageId, postId, filters, comment_text, pagination) {
    const params = new URLSearchParams({
      page: pagination.currentPage,
      per_page: pagination.perPage,
      ...(comment_text && { comment_text }),
      ...(filters.sort && { sort: filters.sort }),
      ...(filters.classification && { classification: filters.classification }),
      ...(filters.customClassifications &&
        filters.customClassifications.length && {
          custom_classifications: filters.customClassifications.join(','),
        }),
    });
    return axios.get(
      `${this.url}/${pageId}/posts/${postId}/comments?${params.toString()}`
    );
  }

  getComments(filters, commentText, pagination, tab = 'pending') {
    const params = new URLSearchParams({
      page: pagination.currentPage,
      per_page: pagination.perPage,
      tab: tab,
      reply_by_ai: filters.reply_by_ai,
      ...(commentText && { comment_text: commentText }),
      ...(filters.sort && { sort: filters.sort }),
      ...(filters.filter && { filter: filters.filter }),
      ...(filters.classification && { classification: filters.classification }),
      ...(filters.purchaseIntention && {
        purchase_intention: filters.purchaseIntention,
      }),
      ...(filters.brand && { brand: filters.brand }),
      ...(filters.label &&
        filters.label.length && {
          label: filters.label.join(','),
        }),
      ...(filters.origin && { media_product_type: filters.origin }),
      ...(filters.platform && { platform: filters.platform }),
      ...(filters.customClassifications &&
        filters.customClassifications.length && {
          custom_classifications: filters.customClassifications.join(','),
        }),
      ...(filters.dateInterval.start &&
        filters.dateInterval.end && {
          startInterval: filters.dateInterval.start,
          endInterval: filters.dateInterval.end,
        }),
    });
    return axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/comments_moderation?${params.toString()}`
    );
  }

  getCustomClassifications() {
    return axios.get(
      `/api/v1/accounts/${this.accountIdFromRoute}/comments_moderation/custom_classifications`
    );
  }

  createReply(pageId, postId, commentId, message) {
    return axios.post(
      `${this.url}/${pageId}/posts/${postId}/comments/${commentId}/create_reply`,
      { message }
    );
  }

  hideComment(pageId, postId, commentId) {
    return axios.put(
      `${this.url}/${pageId}/posts/${postId}/comments/${commentId}/hide`
    );
  }

  deleteComment(pageId, postId, commentId) {
    return axios.delete(
      `${this.url}/${pageId}/posts/${postId}/comments/${commentId}`
    );
  }

  verifyCommentExists(pageId, postId, commentId) {
    return axios.get(
      `${this.url}/${pageId}/posts/${postId}/comments/${commentId}/verify_exists`
    );
  }

  testMode(pageId, postId, message) {
    return axios.post(
      `${this.url}/${pageId}/posts/${postId}/comments/test_mode`,
      { message }
    );
  }

  fetchTestModeHistory(pageId, postId, isFake, pagination) {
    const params = new URLSearchParams({
      page: pagination.currentPage,
      per_page: pagination.perPage,
      ...(isFake && { fake: isFake }),
    });
    return axios.get(
      `${this.url}/${pageId}/posts/${postId}/comments?${params.toString()}`
    );
  }

  sendDirectMessage(commentId, message) {
    return axios.post(
      `/api/v1/accounts/${this.accountIdFromRoute}/comments_moderation/${commentId}/send_direct_message`,
      { message }
    );
  }
}

export default new FacebookPagesAPI();
